import * as THREE from "three"

export type ModelProperties = {
  stress: number
  viscosity: number
  pressure: number
  temperature: number
  torque: number
}

export type PropertyKey = keyof ModelProperties
export const PROPERTY_KEYS = [
  "stress",
  "viscosity",
  "pressure",
  "temperature",
  "torque",
] as const satisfies Readonly<PropertyKey[]>

export const RANGES = {
  stress: [0, 1],
  viscosity: [100, 130],
  pressure: [10, 25],
  temperature: [20, 50],
  torque: [0, 6],
} satisfies Record<PropertyKey, [number, number]>

export type GradientStop = { offset: number; color: string }
export type GradientStops = GradientStop[]

export const GRADIENTS = {
stress: [
  { offset: 0.0, color: "#313695" }, 
  { offset: 0.3, color: "#74add1" }, 
  { offset: 0.6, color: "#fdae61" }, 
  { offset: 1.0, color: "#d73027" }, 
],

viscosity: [
  { offset: 0.0, color: "#006837" }, 
  { offset: 0.4, color: "#31a354" }, 
  { offset: 0.7, color: "#c2e699" }, 
  { offset: 1.0, color: "#ffffcc" }, 
],

pressure: [
  { offset: 0.0, color: "#f7fcf0" }, 
  { offset: 0.3, color: "#7fcdbb" }, 
  { offset: 0.6, color: "#2c7fb8" }, 
  { offset: 1.0, color: "#253494" }, 
],

temperature: [
  { offset: 0.0, color: "#313695" }, 
  { offset: 0.25, color: "#4575b4" },
  { offset: 0.5, color: "#fee090" }, 
  { offset: 0.75, color: "#f46d43" },
  { offset: 1.0, color: "#d73027" }, 
],

torque: [
  { offset: 0.0, color: "#ffffcc" }, 
  { offset: 0.3, color: "#fdcc8a" }, 
  { offset: 0.6, color: "#fc8d59" }, 
  { offset: 1.0, color: "#b30000" }, 
],

} satisfies Record<PropertyKey, GradientStops>

export const PROPERTY_OPTIONS = PROPERTY_KEYS.map(k => ({ value: k, label: capitalize(k) })) 

export function normalize(prop: PropertyKey, value: number): number {
  const [min, max] = RANGES[prop]
  const t = (value - min) / Math.max(1e-9, max - min)
  return Math.pow(THREE.MathUtils.clamp(t, 0, 1), 0.9)
}

export function sampleGradient(stops: GradientStops, t: number): [number, number, number] {
  if (stops.length === 0) return [1, 1, 1]
  t = THREE.MathUtils.clamp(t, 0, 1)
  let i = 0
  while (i < stops.length - 2 && t > stops[i + 1].offset) i++
  const a = stops[i], b = stops[i + 1]
  const span = Math.max(1e-6, b.offset - a.offset)
  const lt = (t - a.offset) / span

  const ca = new THREE.Color(a.color).convertSRGBToLinear()
  const cb = new THREE.Color(b.color).convertSRGBToLinear()
  const c = ca.lerp(cb, lt) 

  return [c.r, c.g, c.b]
}

export function colorFor(prop: PropertyKey, value: number): [number, number, number] {
  const t = normalize(prop, value)
  return sampleGradient(GRADIENTS[prop], t)
}

export function buildColorBuffer<T extends Partial<ModelProperties>>(
  items: T[],
  prop: PropertyKey,
  getValue: (item: T) => number = (it) => (it[prop] as number) ?? 0
): Float32Array {
  const out = new Float32Array(items.length * 3)
  let k = 0
  const stops = GRADIENTS[prop]
  for (const it of items) {
    const [r, g, b] = colorFor(prop, getValue(it))
    out[k++] = r; out[k++] = g; out[k++] = b
  }
  return out
}

export type RGB = [number, number, number]
export function makeLUT(stops: GradientStops, n = 256): RGB[] {
  const lut: RGB[] = []
  for (let i = 0; i < n; i++) lut.push(sampleGradient(stops, i / (n - 1)))
  return lut
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
